<script lang="ts" setup generic="TRow extends Record<string, unknown>">
import type {
  PaginationQueryAction,
  PaginationQueryState,
  PaginationTableColumn,
} from '../types';
import PaginationTableView from '../views/PaginationTableView.vue';
import { usePaginationTableLogic } from './usePaginationTableLogic';

export type PaginationTableProps<TRow extends Record<string, unknown>> = {
  columns: PaginationTableColumn<TRow>[];
  rows: TRow[];
  rowClass?: (row: TRow) => string | Record<string, boolean> | undefined;
  totalItems: number;
  initialPage?: number;
  initialPageSize?: number;
  pageSizeOptions?: number[];
  /** Must be an odd number >= 5. Defaults to 7. */
  maxPageButtons?: number;
  /** When provided, the table syncs its sort/page state to this value reactively. */
  query?: PaginationQueryState;
  isLoading?: boolean;
};

export type PaginationTableEmits = {
  (event: 'query-change', payload: PaginationQueryAction): void;
};

const props = defineProps<PaginationTableProps<TRow>>();
const emit = defineEmits<
  PaginationTableEmits & {
    (event: 'row-click', row: TRow): void;
  }
>();

const { viewProps, onAction } = usePaginationTableLogic(props, (action) =>
  emit('query-change', action),
);
</script>

<template>
  <PaginationTableView
    v-bind="viewProps"
    @action="onAction"
    @row-click="emit('row-click', $event)"
  >
    <template #cell="slotProps">
      <slot name="cell" v-bind="slotProps" />
    </template>
    <template v-for="(_, name) in $slots" #[name]="slotProps">
      <slot :name="name" v-bind="slotProps ?? {}" />
    </template>
  </PaginationTableView>
</template>
